/**
 * ViewInvoiceDialog Component
 * Dialog for viewing closed invoice details
 */

import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Separator } from "../../components/ui/separator";

export function ViewInvoiceDialog({ 
  open, 
  onOpenChange, 
  invoice,
  formatCurrency,
  getStatusBadge
}) {
  if (!invoice) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="view-invoice-dialog">
        <DialogHeader>
          <DialogTitle className="font-mono">{invoice?.invoice_number}</DialogTitle>
          <DialogDescription>
            {invoice?.created_at && new Date(invoice.created_at).toLocaleString()}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Status & Customer Info */}
          <div className="flex justify-between items-center">
            {getStatusBadge(invoice.status)}
            <Badge variant="outline">{invoice.order_type === "dine_in" ? "Dine In" : "Takeaway"}</Badge>
          </div>
          
          {invoice.customer_name && invoice.customer_name !== "Walk-in" && (
            <div className="bg-muted p-3 rounded-lg">
              <p className="text-xs text-muted-foreground">Customer</p>
              <p className="font-medium">{invoice.customer_name}</p>
              {invoice.customer_phone && (
                <p className="text-sm text-muted-foreground">{invoice.customer_phone}</p>
              )}
            </div>
          )}

          {/* Items */}
          <div className="border rounded-lg">
            <div className="bg-muted px-3 py-2 border-b">
              <p className="text-sm font-medium">Items ({invoice.items?.length || 0})</p>
            </div>
            <div className="divide-y max-h-48 overflow-y-auto">
              {invoice.items?.map((item, idx) => (
                <div key={idx} className="flex justify-between items-center p-3">
                  <div>
                    <p className="text-sm font-medium">{item.name}</p>
                    <p className="text-xs text-muted-foreground">× {item.quantity}</p>
                  </div>
                  <p className="font-mono text-sm">{formatCurrency(item.price * item.quantity)}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Totals */}
          <div className="space-y-2 pt-2 border-t">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Subtotal</span>
              <span className="font-mono">{formatCurrency(invoice.subtotal)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground flex items-center gap-1">
                Tax
                {invoice.tax_calculation_method === 'inclusive' && (
                  <span className="text-xs bg-blue-100 text-blue-800 px-1 rounded">incl.</span>
                )}
              </span>
              <span className="font-mono">{formatCurrency(invoice.tax)}</span>
            </div>
            {invoice.loyalty_discount > 0 && (
              <div className="flex justify-between text-sm text-green-600">
                <span>Loyalty Discount</span>
                <span className="font-mono">-{formatCurrency(invoice.loyalty_discount)}</span>
              </div>
            )}
            {invoice.discount > 0 && (
              <div className="flex justify-between text-sm text-green-600">
                <span className="flex items-center gap-1">
                  Discount
                  {invoice.discount_code && (
                    <span className="text-xs bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 px-1 rounded">
                      {invoice.discount_code}
                    </span>
                  )}
                </span>
                <span className="font-mono">-{formatCurrency(invoice.discount)}</span>
              </div>
            )}
            <Separator />
            <div className="flex justify-between font-bold">
              <span>Total</span>
              <span className="font-mono text-primary">{formatCurrency(invoice.total)}</span>
            </div>
            {invoice.amount_paid && invoice.amount_paid !== invoice.total && (
              <div className="flex justify-between text-sm text-blue-600">
                <span>Amount Paid</span>
                <span className="font-mono">{formatCurrency(invoice.amount_paid)}</span>
              </div>
            )}
          </div>

          {/* Payment Info */}
          {invoice.payment_method && (
            <div className="bg-muted p-3 rounded-lg space-y-1">
              <p className="text-xs text-muted-foreground">Payment Method</p>
              <p className="font-medium capitalize">
                {invoice.payment_method === 'mpesa_c2b' ? 'M-Pesa (Paybill)' : 
                 invoice.payment_method === 'mpesa' ? 'M-Pesa' : 
                 invoice.payment_method}
                {invoice.is_manual_payment && (
                  <Badge variant="secondary" className="ml-2 text-[10px]">Manual</Badge>
                )}
              </p>
              {invoice.mpesa_receipt_number && (
                <p className="text-xs font-mono text-muted-foreground">
                  Ref: {invoice.mpesa_receipt_number}
                </p>
              )}
              {invoice.manual_ref && (
                <p className="text-xs font-mono text-muted-foreground" data-testid="manual-ref-display">
                  Ref: {invoice.manual_ref}
                </p>
              )}
              {invoice.card_last4 && (
                <p className="text-xs text-muted-foreground" data-testid="card-last4-display">
                  Card: ****{invoice.card_last4}
                </p>
              )}
              {invoice.card_transaction_id && (
                <p className="text-xs font-mono text-muted-foreground">
                  Transaction: {invoice.card_transaction_id}
                </p>
              )}
              {invoice.completed_at && (
                <p className="text-xs text-muted-foreground mt-1">
                  Paid: {new Date(invoice.completed_at).toLocaleString()}
                </p>
              )}
            </div>
          )}
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="close-invoice-btn">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
